// Public states — снапшоты project'а под well. Список + детальный
// редактор с casings (MD-диапазон) и muds (scope + md_point).
//
// Паттерн:
//   * list — по образцу StatesPage.tsx (таблица + refetch)
//   * detail — по образцу DrillstringPage.tsx (header + section-list с
//     catalog-селектором и up/down для перестановки place)
//
// Save: PATCH header (если изменился), PUT /casings (если изменились),
// PUT /muds (если изменились). Каждый PUT возвращает свежий PublicStateDetailDto,
// но мы тянем refetch для консистентности с server-сертированным `place`.
//
// Drag&drop сознательно не реализован — для casings порядок ≠ MD-порядок
// (UI можно отсортировать как угодно), для muds place per-scope. Up/down
// проще и достаточно. DnD добавляется отдельной задачей если понадобится.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup, batch } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listPublicStates,
  getPublicState,
  createPublicState,
  updatePublicStateHeader,
  deletePublicState,
  replaceStateCasings,
  replaceStateMuds,
  MUD_SCOPES,
} from './api/public_states'
import type {
  PublicStateSummaryDto,
  PublicStateDetailDto,
  PublicStateCasingInput,
  PublicStateMudInput,
} from './api/public_states'
import { listCasings } from './api/casings'
import type { CasingDto } from './api/casings'
import { listMuds } from './api/muds'
import type { MudDto } from './api/muds'
import { listWells } from './api/wells'

// ────────────────────── helpers ──────────────────────

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString('ru-RU', {
      year:   'numeric',
      month:  '2-digit',
      day:    '2-digit',
      hour:   '2-digit',
      minute: '2-digit',
    })
  } catch {
    return s
  }
}

let nextUid = 1
const newUid = () => nextUid++

// ────────────────────── editor drafts ──────────────────────

// Локальные draft-типы держат значения как строки — input хранит текст,
// парсим на submit. uid — стабильный ключ для <For>.
type CasingDraft = {
  uid: number
  casing_id: number | null
  md_start_m: string
  md_end_m: string
}

type MudDraft = {
  uid: number
  mud_id: number | null
  scope: string
  md_point_m: string
}

function casingDraftFrom(s: { casing_id: number; md_start_m: number; md_end_m: number }): CasingDraft {
  return {
    uid:        newUid(),
    casing_id:  s.casing_id,
    md_start_m: String(s.md_start_m),
    md_end_m:   String(s.md_end_m),
  }
}

function mudDraftFrom(s: { mud_id: number; scope: string; md_point_m: number }): MudDraft {
  return {
    uid:        newUid(),
    mud_id:     s.mud_id,
    scope:      s.scope,
    md_point_m: String(s.md_point_m),
  }
}

// ────────────────────── editor ──────────────────────

type EditorProps = {
  publicStateId: number | null  // null = создание
  onBack:        () => void
  onCreated:     (id: number) => void
}

const PublicStateEditor: Component<EditorProps> = (props) => {
  const [casingsCatalog] = createResource(listCasings)
  const [mudsCatalog] = createResource(listMuds)
  const [rigs] = createResource(listWells)

  const [detail, { refetch: refetchDetail }] = createResource(
    () => props.publicStateId,
    async (id) => (id == null ? null : await getPublicState(id)),
  )

  const [name, setName] = createSignal('')
  const [description, setDescription] = createSignal('')
  // Для create — выбираем рiг; в edit это поле readonly (привязка фиксирована).
  const [publicRigId, setWellId] = createSignal<number | null>(null)
  const [casings, setCasings] = createSignal<CasingDraft[]>([])
  const [muds, setMuds] = createSignal<MudDraft[]>([])
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => props.publicStateId == null

  // Заливаем detail в локальный стейт — пересчёт при смене detail()
  // (после refetch или открытия другой записи).
  createMemo(() => {
    const d = detail()
    if (d) {
      batch(() => {
        setName(d.name)
        setDescription(d.description)
        setWellId(d.well_id)
        setCasings(d.casings.map(casingDraftFrom))
        setMuds(d.muds.map(mudDraftFrom))
      })
    } else if (isCreate() && !detail.loading) {
      // Свежее создание: предзаполняем рiг первым из списка (если есть).
      // Юзер сможет переключить через select.
      batch(() => {
        setName('')
        setDescription('')
        const list = rigs() ?? []
        setWellId(list[0]?.id ?? null)
        setCasings([])
        setMuds([])
      })
    }
  })

  // ────── dirty ──────

  const sameCasings = (a: CasingDraft[], b: PublicStateDetailDto['casings']): boolean => {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const x = a[i]!
      const y = b[i]!
      if (x.casing_id !== y.casing_id) return false
      if (x.md_start_m !== String(y.md_start_m)) return false
      if (x.md_end_m !== String(y.md_end_m)) return false
    }
    return true
  }

  const sameMuds = (a: MudDraft[], b: PublicStateDetailDto['muds']): boolean => {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      const x = a[i]!
      const y = b[i]!
      if (x.mud_id !== y.mud_id) return false
      if (x.scope !== y.scope) return false
      if (x.md_point_m !== String(y.md_point_m)) return false
    }
    return true
  }

  const isDirty = createMemo<boolean>(() => {
    if (isCreate()) {
      return (
        name().trim().length > 0 ||
        description().trim().length > 0 ||
        casings().length > 0 ||
        muds().length > 0
      )
    }
    const d = detail()
    if (!d) return false
    if (d.name !== name()) return true
    if (d.description !== description()) return true
    if (!sameCasings(casings(), d.casings)) return true
    if (!sameMuds(muds(), d.muds)) return true
    return false
  })

  const tryBack = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Продолжить?')) return
    props.onBack()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryBack()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // ────── мутации drafts ──────

  const addCasing = () =>
    setCasings([
      ...casings(),
      { uid: newUid(), casing_id: null, md_start_m: '', md_end_m: '' },
    ])
  const removeCasing = (uid: number) => setCasings(casings().filter((s) => s.uid !== uid))
  const updateCasing = (uid: number, patch: Partial<CasingDraft>) =>
    setCasings(casings().map((s) => (s.uid === uid ? { ...s, ...patch } : s)))
  const moveCasing = (uid: number, dir: -1 | 1) => {
    const arr = casings().slice()
    const idx = arr.findIndex((s) => s.uid === uid)
    const ni = idx + dir
    if (idx < 0 || ni < 0 || ni >= arr.length) return
    const [item] = arr.splice(idx, 1)
    arr.splice(ni, 0, item!)
    setCasings(arr)
  }

  const addMud = (scope: string) =>
    setMuds([
      ...muds(),
      { uid: newUid(), mud_id: null, scope, md_point_m: '' },
    ])
  const removeMud = (uid: number) => setMuds(muds().filter((s) => s.uid !== uid))
  const updateMud = (uid: number, patch: Partial<MudDraft>) =>
    setMuds(muds().map((s) => (s.uid === uid ? { ...s, ...patch } : s)))
  const moveMud = (uid: number, dir: -1 | 1) => {
    const arr = muds().slice()
    const idx = arr.findIndex((s) => s.uid === uid)
    if (idx < 0) return
    // Перестановка только в пределах своего scope-сегмента, чтобы поведение
    // up/down совпадало с тем, что видит пользователь (мы рендерим muds
    // двумя группами outer/inner). Ищем соседа того же scope.
    const item = arr[idx]!
    const sameScopeIdxs = arr
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.scope === item.scope)
      .map(({ i }) => i)
    const place = sameScopeIdxs.indexOf(idx)
    const newPlace = place + dir
    if (newPlace < 0 || newPlace >= sameScopeIdxs.length) return
    const swapWith = sameScopeIdxs[newPlace]!
    const [removed] = arr.splice(idx, 1)
    arr.splice(swapWith, 0, removed!)
    setMuds(arr)
  }

  // ────── сбор inputs + валидация ──────

  const collectCasings = (): PublicStateCasingInput[] | string => {
    const arr = casings()
    const out: PublicStateCasingInput[] = []
    for (let i = 0; i < arr.length; i++) {
      const s = arr[i]!
      if (s.casing_id == null) return `Обсадка #${i + 1}: выберите типоразмер`
      const md_start_m = Number(s.md_start_m)
      const md_end_m = Number(s.md_end_m)
      if (!Number.isFinite(md_start_m)) return `Обсадка #${i + 1}: MD начало — ожидается число`
      if (!Number.isFinite(md_end_m)) return `Обсадка #${i + 1}: MD конец — ожидается число`
      if (!(md_end_m > md_start_m)) return `Обсадка #${i + 1}: MD конец должен быть > MD начала`
      out.push({ casing_id: s.casing_id, md_start_m, md_end_m })
    }
    return out
  }

  const collectMuds = (): PublicStateMudInput[] | string => {
    const arr = muds()
    const out: PublicStateMudInput[] = []
    for (let i = 0; i < arr.length; i++) {
      const s = arr[i]!
      if (s.mud_id == null) return `Раствор #${i + 1}: выберите типоразмер`
      const md_point_m = Number(s.md_point_m)
      if (!Number.isFinite(md_point_m)) return `Раствор #${i + 1}: MD точка — ожидается число`
      out.push({ mud_id: s.mud_id, scope: s.scope, md_point_m })
    }
    return out
  }

  // ────── save ──────

  const onSave = async () => {
    setErr(null)
    const trimmedName = name().trim()
    if (trimmedName.length === 0) {
      setErr('Название обязательно')
      return
    }

    const cs = collectCasings()
    if (typeof cs === 'string') { setErr(cs); return }
    const ms = collectMuds()
    if (typeof ms === 'string') { setErr(ms); return }

    if (isCreate()) {
      const rid = publicRigId()
      if (rid == null) {
        setErr('Выберите рiг')
        return
      }

      setBusy(true)
      try {
        const created = await createPublicState({
          well_id: rid,
          name:          trimmedName,
          description:   description(),
          casings:       cs,
          muds:          ms,
        })
        props.onCreated(created.id)
        return
      } catch (e) {
        setErr(String(e))
      } finally {
        setBusy(false)
      }
      return
    }

    // edit: PATCH header + PUT/PUT по diff
    const id = props.publicStateId!
    const d = detail()!

    setBusy(true)
    try {
      const headerChanged =
        d.name !== trimmedName || d.description !== description()
      const casingsChanged = !sameCasings(casings(), d.casings)
      const mudsChanged = !sameMuds(muds(), d.muds)

      if (headerChanged) {
        await updatePublicStateHeader(id, {
          name:        trimmedName,
          description: description(),
        })
      }
      if (casingsChanged) {
        await replaceStateCasings(id, { casings: cs })
      }
      if (mudsChanged) {
        await replaceStateMuds(id, { muds: ms })
      }
      // Полный refetch — берём свежие revision/updated_at + перенумерованный place.
      await refetchDetail()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async () => {
    if (isCreate()) return
    if (!confirm(`Удалить state «${name()}»?`)) return
    setBusy(true)
    setErr(null)
    try {
      await deletePublicState(props.publicStateId!)
      props.onBack()
    } catch (e) {
      setErr(String(e))
      setBusy(false)
    }
  }

  // ────── lookups для рендеринга ──────

  const casingsById = createMemo<Record<number, CasingDto>>(() => {
    const map: Record<number, CasingDto> = {}
    for (const c of casingsCatalog() ?? []) map[c.id] = c
    for (const c of detail()?.casings_resolved ?? []) map[c.id] = c
    return map
  })
  const mudsById = createMemo<Record<number, MudDto>>(() => {
    const map: Record<number, MudDto> = {}
    for (const m of mudsCatalog() ?? []) map[m.id] = m
    for (const m of detail()?.muds_resolved ?? []) map[m.id] = m
    return map
  })

  const casingSummary = (s: CasingDraft): string => {
    if (s.casing_id == null) return '—'
    const c = casingsById()[s.casing_id]
    if (!c) return `#${s.casing_id} · отсутствует`
    return `${c.name} · D=${c.external_diam_m} · δ=${c.wall_thickness_m}`
  }
  const mudSummary = (s: MudDraft): string => {
    if (s.mud_id == null) return '—'
    const m = mudsById()[s.mud_id]
    if (!m) return `#${s.mud_id} · отсутствует`
    return `${m.name} · ρ=${m.density_kg_m3} · θ600=${m.theta_o600}/θ300=${m.theta_o300}`
  }

  // muds разделяем на outer/inner для отдельных секций UI.
  const outerMuds = createMemo<MudDraft[]>(() => muds().filter((m) => m.scope === 'outer'))
  const innerMuds = createMemo<MudDraft[]>(() => muds().filter((m) => m.scope === 'inner'))

  return (
    <div class="page page-wide">
      <div class="page-header">
        <div class="row" style={{ gap: 'var(--sp-3)' }}>
          <button class="btn btn-ghost" onClick={tryBack} disabled={busy()}>
            ← К списку
          </button>
          <div>
            <h1 class="page-title">
              {isCreate() ? 'Новый state' : `State #${props.publicStateId}`}
            </h1>
            <p class="page-subtitle">
              Снапшот project'а: header + список обсадки и растворов
              <Show when={isDirty()}>
                <span style={{ 'margin-left': 'var(--sp-3)', color: 'var(--status-warn)' }}>
                  · несохранённые изменения
                </span>
              </Show>
            </p>
          </div>
        </div>
        <div class="row">
          <Show when={!isCreate()}>
            <button class="btn btn-ghost" onClick={onDelete} disabled={busy()}>
              <Icon name="x" />Удалить
            </button>
          </Show>
          <button
            class="btn btn-primary"
            onClick={onSave}
            disabled={busy() || !isDirty()}
          >
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {err()}
        </div>
      </Show>

      <Show when={detail.loading || rigs.loading || casingsCatalog.loading || mudsCatalog.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={detail.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка загрузки: {String(detail.error)}
        </div>
      </Show>

      <Show when={!detail.loading && !detail.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', 'margin-bottom': 'var(--sp-5)' }}>
          <div style={{ display: 'flex', 'flex-direction': 'column', gap: 'var(--sp-4)' }}>
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder="Напр., 2-й интервал · 17.05.2026"
              />
            </label>
            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={2}
                value={description()}
                onInput={(e) => setDescription(e.currentTarget.value)}
                placeholder="Произвольное примечание"
              />
            </label>
            <label class="field">
              <div class="field-label">Рiг</div>
              <Show when={isCreate()} fallback={
                <input
                  class="field-input"
                  value={(rigs() ?? []).find((r) => r.id === publicRigId())?.name ?? `#${publicRigId() ?? ''}`}
                  readOnly
                  disabled
                />
              }>
                <select
                  class="field-input"
                  value={publicRigId() ?? ''}
                  onChange={(e) => setWellId(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))}
                >
                  <option value="">— выберите —</option>
                  <For each={rigs() ?? []}>
                    {(r) => <option value={r.id}>{r.name}</option>}
                  </For>
                </select>
              </Show>
            </label>
          </div>
        </div>

        {/* ───────── casings ───────── */}
        <div class="section-head">
          <h2>Обсадка ({casings().length})</h2>
          <button class="btn" onClick={addCasing}>
            <Icon name="plus" />Добавить обсадку
          </button>
        </div>

        <Show when={(casingsCatalog() ?? []).length === 0}>
          <div class="card" style={{ padding: 'var(--sp-4)', 'margin-bottom': 'var(--sp-3)' }}>
            <div class="muted text-sm">
              Каталог обсадки пуст — добавьте записи в разделе «Каталог обсадки», чтобы привязывать их к state'у.
            </div>
          </div>
        </Show>

        <Show when={casings().length === 0 && (casingsCatalog() ?? []).length > 0}>
          <div class="card" style={{ padding: 'var(--sp-5)', 'text-align': 'center' }}>
            <div class="muted">Нет привязанной обсадки</div>
          </div>
        </Show>

        <Show when={casings().length > 0}>
          <div class="card" style={{ padding: 0, 'margin-bottom': 'var(--sp-5)' }}>
            <table class="tbl">
              <thead>
                <tr>
                  <th style={{ width: '40px' }} />
                  <th>Типоразмер</th>
                  <th style={{ width: '160px' }}>MD начало, м</th>
                  <th style={{ width: '160px' }}>MD конец, м</th>
                  <th style={{ width: '90px' }} />
                </tr>
              </thead>
              <tbody>
                <For each={casings()}>
                  {(s, i) => (
                    <tr>
                      <td class="mono text-xs muted">{i() + 1}</td>
                      <td>
                        <select
                          class="field-input"
                          value={s.casing_id ?? ''}
                          onChange={(e) =>
                            updateCasing(s.uid, {
                              casing_id: e.currentTarget.value === '' ? null : Number(e.currentTarget.value),
                            })
                          }
                        >
                          <option value="">— выберите —</option>
                          <For each={casingsCatalog() ?? []}>
                            {(c) => (
                              <option value={c.id}>
                                {c.name} (D={c.external_diam_m}, δ={c.wall_thickness_m})
                              </option>
                            )}
                          </For>
                        </select>
                        <div class="text-xs muted" style={{ 'margin-top': 'var(--sp-1)' }}>
                          {casingSummary(s)}
                        </div>
                      </td>
                      <td>
                        <input
                          class="field-input mono"
                          value={s.md_start_m}
                          onInput={(e) => updateCasing(s.uid, { md_start_m: e.currentTarget.value })}
                          inputmode="decimal"
                          placeholder="0"
                        />
                      </td>
                      <td>
                        <input
                          class="field-input mono"
                          value={s.md_end_m}
                          onInput={(e) => updateCasing(s.uid, { md_end_m: e.currentTarget.value })}
                          inputmode="decimal"
                          placeholder="0"
                        />
                      </td>
                      <td style={{ 'white-space': 'nowrap' }}>
                        <button class="btn-icon" title="Вверх" onClick={() => moveCasing(s.uid, -1)}>
                          ↑
                        </button>
                        <button class="btn-icon" title="Вниз" onClick={() => moveCasing(s.uid, 1)}>
                          ↓
                        </button>
                        <button class="btn-icon" title="Удалить" onClick={() => removeCasing(s.uid)}>
                          <Icon name="x" />
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        {/* ───────── muds ───────── */}
        <For each={MUD_SCOPES}>
          {(scope) => {
            const list = () => (scope.id === 'outer' ? outerMuds() : innerMuds())
            return (
              <>
                <div class="section-head">
                  <h2>
                    Растворы — {scope.label} ({list().length})
                  </h2>
                  <button class="btn" onClick={() => addMud(scope.id)}>
                    <Icon name="plus" />Добавить раствор
                  </button>
                </div>

                <Show when={(mudsCatalog() ?? []).length === 0}>
                  <div class="card" style={{ padding: 'var(--sp-4)', 'margin-bottom': 'var(--sp-3)' }}>
                    <div class="muted text-sm">
                      Каталог растворов пуст — добавьте записи в разделе «Каталог растворов».
                    </div>
                  </div>
                </Show>

                <Show when={list().length === 0 && (mudsCatalog() ?? []).length > 0}>
                  <div class="card" style={{ padding: 'var(--sp-5)', 'text-align': 'center', 'margin-bottom': 'var(--sp-3)' }}>
                    <div class="muted">Нет привязанных растворов</div>
                  </div>
                </Show>

                <Show when={list().length > 0}>
                  <div class="card" style={{ padding: 0, 'margin-bottom': 'var(--sp-5)' }}>
                    <table class="tbl">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }} />
                          <th>Раствор</th>
                          <th style={{ width: '160px' }}>MD точка, м</th>
                          <th style={{ width: '90px' }} />
                        </tr>
                      </thead>
                      <tbody>
                        <For each={list()}>
                          {(s, i) => (
                            <tr>
                              <td class="mono text-xs muted">{i() + 1}</td>
                              <td>
                                <select
                                  class="field-input"
                                  value={s.mud_id ?? ''}
                                  onChange={(e) =>
                                    updateMud(s.uid, {
                                      mud_id: e.currentTarget.value === '' ? null : Number(e.currentTarget.value),
                                    })
                                  }
                                >
                                  <option value="">— выберите —</option>
                                  <For each={mudsCatalog() ?? []}>
                                    {(m) => (
                                      <option value={m.id}>
                                        {m.name} (ρ={m.density_kg_m3})
                                      </option>
                                    )}
                                  </For>
                                </select>
                                <div class="text-xs muted" style={{ 'margin-top': 'var(--sp-1)' }}>
                                  {mudSummary(s)}
                                </div>
                              </td>
                              <td>
                                <input
                                  class="field-input mono"
                                  value={s.md_point_m}
                                  onInput={(e) => updateMud(s.uid, { md_point_m: e.currentTarget.value })}
                                  inputmode="decimal"
                                  placeholder="0"
                                />
                              </td>
                              <td style={{ 'white-space': 'nowrap' }}>
                                <button class="btn-icon" title="Вверх" onClick={() => moveMud(s.uid, -1)}>
                                  ↑
                                </button>
                                <button class="btn-icon" title="Вниз" onClick={() => moveMud(s.uid, 1)}>
                                  ↓
                                </button>
                                <button class="btn-icon" title="Удалить" onClick={() => removeMud(s.uid)}>
                                  <Icon name="x" />
                                </button>
                              </td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </Show>
              </>
            )
          }}
        </For>
      </Show>
    </div>
  )
}

// ────────────────────── list page ──────────────────────

type PublicStatesPageProps = {
  publicStateId?: number | null
  create?:        boolean
  onOpen:         (id: number) => void
  onCreate:       () => void
  onBack:         () => void
}

export const PublicStatesPage: Component<PublicStatesPageProps> = (props) => {
  const [items, { refetch }] = createResource<PublicStateSummaryDto[]>(listPublicStates)
  const [rigs] = createResource(listWells)
  const [filterText, setFilterText] = createSignal('')

  const inEditor = createMemo<boolean>(() => props.create === true || props.publicStateId != null)

  const rigsById = createMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {}
    for (const r of rigs() ?? []) map[r.id] = r.name
    return map
  })

  const filtered = createMemo<PublicStateSummaryDto[]>(() => {
    const all = items() ?? []
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    return all.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      (s.description ?? '').toLowerCase().includes(q)
    )
  })

  const onCreated = (id: number) => {
    refetch()
    props.onOpen(id)
  }
  const onBackFromEditor = () => {
    refetch()
    props.onBack()
  }

  return (
    <Show
      when={inEditor()}
      fallback={
        <div class="page page-wide">
          <div class="page-header">
            <div>
              <h1 class="page-title">Public states</h1>
              <p class="page-subtitle">
                Снапшоты project'а под well — ваш «облачный» каталог конфигураций.
              </p>
            </div>
            <div class="row">
              <button class="btn btn-primary" onClick={() => props.onCreate()}>
                <Icon name="plus" />Создать state
              </button>
            </div>
          </div>

          <Show when={items.loading}>
            <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
          </Show>

          <Show when={items.error}>
            <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
              Ошибка: {String(items.error)}
            </div>
          </Show>

          <Show when={items() && items()!.length === 0 && !items.loading}>
            <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
              <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
                State'ов ещё нет
              </div>
              <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
                Создайте первый снапшот project'а
              </div>
              <button class="btn btn-primary" onClick={() => props.onCreate()}>
                <Icon name="plus" />Создать state
              </button>
            </div>
          </Show>

          <Show when={items() && items()!.length > 0}>
            <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
              <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
                <Icon name="search" />
                <input
                  placeholder="Поиск по названию / описанию"
                  value={filterText()}
                  onInput={(e) => setFilterText(e.currentTarget.value)}
                />
                <Show when={filterText().length > 0}>
                  <button
                    class="btn-icon btn-ghost"
                    style={{ width: '24px', height: '24px' }}
                    title="Очистить"
                    onClick={() => setFilterText('')}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </Show>
              </div>
              <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
                Показано {filtered().length} из {items()!.length}
              </div>
            </div>

            <div class="card">
              <table class="tbl">
                <thead>
                  <tr>
                    <th style={{ width: '90px' }}>ID</th>
                    <th>Название</th>
                    <th style={{ width: '180px' }}>Рiг</th>
                    <th style={{ width: '70px' }}>rev</th>
                    <th style={{ width: '160px' }}>Изменён</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(s) => (
                      <tr style={{ cursor: 'pointer' }} onClick={() => props.onOpen(s.id)}>
                        <td class="mono text-xs muted">#{s.id}</td>
                        <td style={{ 'font-weight': 500 }}>
                          {s.name}
                          <Show when={s.description}>
                            <div class="text-xs muted" style={{ 'margin-top': 'var(--sp-1)' }}>
                              {s.description}
                            </div>
                          </Show>
                        </td>
                        <td>{rigsById()[s.well_id] ?? `#${s.well_id}`}</td>
                        <td class="mono text-xs">{s.revision}</td>
                        <td class="text-xs muted">{formatDate(s.updated_at)}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <Show when={filtered().length === 0}>
                <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
                  Ничего не найдено по фильтру
                </div>
              </Show>
            </div>
          </Show>
        </div>
      }
    >
      <PublicStateEditor
        publicStateId={props.publicStateId ?? null}
        onBack={onBackFromEditor}
        onCreated={onCreated}
      />
    </Show>
  )
}
